import * as React from "react";
import LegacyFormDropDownListPaginate from "@/components/Kendo/Form/FromDropDownListPagination";
import { useCoerceToArrayValue } from "./useCoerceToArrayValue";
import { useControlledField, type FieldValidator } from "./useControlledField";

/**
 * RHF wrapper around the existing paginated dropdown. The underlying component
 * still consumes a Kendo FieldRenderProps shape; we synthesize value/onChange
 * via useControlledField. Keeps the legacy implementation in place — once that
 * is migrated to RHF natively, this wrapper can collapse to a re-export.
 */
type LegacyProps = React.ComponentProps<typeof LegacyFormDropDownListPaginate>;

export interface FormDropDownListPaginateProps
  extends Omit<LegacyProps, "name" | "value" | "onChange"> {
  name: string;
  validator?: FieldValidator;
}

export const FormDropDownListPaginate: React.FC<
  FormDropDownListPaginateProps
> = ({ name, validator, isMultiValue, ...rest }) => {
  const { field, fieldState } = useControlledField({ name, validator });

  // `isMultiValue` is optional, and passing it through as `undefined` would hit
  // `useCoerceToArrayValue`'s `enabled = true` default — which resets any
  // single-value selection to `[]` the moment the user picks something.
  useCoerceToArrayValue(name, field.value, !!isMultiValue);

  return (
    <LegacyFormDropDownListPaginate
      {...(rest as LegacyProps)}
      name={name}
      isMultiValue={isMultiValue}
      value={field.value}
      onChange={(event: { target?: unknown; value?: unknown }) =>
        field.onChange(event.value)
      }
      onBlur={field.onBlur}
      valid={!fieldState.error}
      validationMessage={fieldState.error?.message}
      touched={fieldState.isTouched}
    />
  );
};

export default FormDropDownListPaginate;
